import { useCallback, useContext } from 'react';

import { useSchedule } from '@app/components/Schedule';
import { DEFAULT_SOCIAL_INTEGRATION_ID_INDEX, SUCCESS } from '@app/constants';
import type { NSendMessageModal } from '@app/features/SendoutCreationModal/components/SendMessageModal/SendMessageModal.types';
import { NotificationContext } from '@app/providers';
import {
  useCreateSendoutMutation,
  useLazyPreviewSendoutMessagesQuery,
} from '@app/services/rtkQuery/Sendouts/api';
import type { NSendoutsApi } from '@app/services/rtkQuery/Sendouts/types';
import { fileToBase64 } from '@app/utils';

export const useCreateSendout = (
  data: NSendMessageModal.SendMessageModalState
) => {
  const {
    usage,
    actionType,
    activeBranchId,
    selectedIds,
    searchId,
    getSearchId,
  } = data;
  const { showNotification } = useContext(NotificationContext);
  const { getScheduleDate } = useSchedule();

  const [createSendout, { isLoading: isCreateSendoutLoading }] =
    useCreateSendoutMutation();

  const [previewSendoutMessages] = useLazyPreviewSendoutMessagesQuery();

  const createSendoutHandler = useCallback(
    async (values: NSendMessageModal.SendMessageFormValues) => {
      const {
        files,
        frequency,
        message,
        channel,
        integration,
        tags,
        schedule,
        timezone,
        useSchedule,
      } = values;
      const attachments: NSendoutsApi.CreateSendoutRequest['Attachments'] =
        [] as unknown as NSendoutsApi.CreateSendoutRequest['Attachments'];

      for (const file of files) {
        const Data = await fileToBase64(file.originFileObj as File);
        attachments.push({
          Name: file.name,
          Data,
        });
      }

      const clientIds = actionType === 'selected' ? selectedIds : [];
      // Группа поиска берётся на момент отправки: снимок, сделанный при
      // открытии формы, к этому времени мог быть удалён на сервере новым
      // поиском реестра (issue/11096).
      const currentSearchId = getSearchId?.() ?? searchId;

      try {
        await createSendout({
          ActiveBranchId: activeBranchId,
          Name: '',
          Message: message,
          ChannelId: channel,
          IntegrationId:
            integration === DEFAULT_SOCIAL_INTEGRATION_ID_INDEX
              ? null
              : Number(integration),
          SendoutDelaySec: frequency,
          TagIds: tags,
          leadsSearchId:
            usage === 'leads' && actionType === 'all'
              ? currentSearchId ?? '0'
              : '0',
          Attachments: attachments,
          ClientIds: clientIds,
          clientsSearchId:
            usage === 'clients' && actionType === 'all'
              ? currentSearchId ?? '0'
              : '0',
          Timezone: timezone ?? 'Europe/Moscow',
          MoS: useSchedule ? getScheduleDate(schedule, 'monday') : null,
          MoE: useSchedule
            ? getScheduleDate(schedule, 'monday', 'endTime')
            : null,
          TuS: useSchedule ? getScheduleDate(schedule, 'tuesday') : null,
          TuE: useSchedule
            ? getScheduleDate(schedule, 'tuesday', 'endTime')
            : null,
          WeS: useSchedule ? getScheduleDate(schedule, 'wednesday') : null,
          WeE: useSchedule
            ? getScheduleDate(schedule, 'wednesday', 'endTime')
            : null,
          ThS: useSchedule ? getScheduleDate(schedule, 'thursday') : null,
          ThE: useSchedule
            ? getScheduleDate(schedule, 'thursday', 'endTime')
            : null,
          FrS: useSchedule ? getScheduleDate(schedule, 'friday') : null,
          FrE: useSchedule
            ? getScheduleDate(schedule, 'friday', 'endTime')
            : null,
          SaS: useSchedule ? getScheduleDate(schedule, 'saturday') : null,
          SaE: useSchedule
            ? getScheduleDate(schedule, 'saturday', 'endTime')
            : null,
          SuS: useSchedule ? getScheduleDate(schedule, 'sunday') : null,
          SuE: useSchedule
            ? getScheduleDate(schedule, 'sunday', 'endTime')
            : null,
          IsNoSchedule: !useSchedule,
        }).unwrap();

        showNotification('Рассылка создана.', SUCCESS);

        return Promise.resolve();
      } catch (error) {
        showNotification(error);
        return Promise.reject();
      }
    },
    [
      actionType,
      activeBranchId,
      createSendout,
      getScheduleDate,
      getSearchId,
      searchId,
      selectedIds,
      showNotification,
      usage,
    ]
  );
  return {
    createSendoutHandler,
    previewSendoutMessages,
    isCreateSendoutLoading,
  };
};
